import { useEffect } from 'react';
import urlTemplate from 'url-template';
import type { LocationsResponse } from 'src/apis/aggregate-eu/types';
import type { AggregateEuRootRequest } from 'src/apis/aggregate-eu/use-aggregate-eu-root';
import type { Successful } from 'src/hooks/use-axios';
import { useAxios } from 'src/hooks/use-axios';
import { Forbidden } from 'src/utils/errors';

type HookConfig = {
  aggregateEuRootRequest: Successful<AggregateEuRootRequest>;
};

export function useGetLocations({ aggregateEuRootRequest }: HookConfig) {
  const locationsLink =
    aggregateEuRootRequest.response.data._links?.getLocationOptions?.href;

  const locations = useAxios(
    (axios, baseConfig) => {
      if (!locationsLink) throw new Forbidden();
      const url = urlTemplate.parse(locationsLink).expand({});

      return axios.request<LocationsResponse>({
        ...baseConfig,
        url,
      });
    },
    {
      neededOnPageLoad: true,
      onError(error) {
        if (error instanceof Forbidden) {
          return error;
        } else {
          throw error;
        }
      },
    }
  );

  useEffect(() => {
    if (!locationsLink) return;

    locations.execute();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationsLink]);

  return locations;
}

export type GetLocationsRequest = ReturnType<typeof useGetLocations>;
